import React from 'react';
import {Table, Typography, Tag, Badge, TableColumnsType, Row, Dropdown, Space, MenuProps, Button} from 'antd';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';
import {DownOutlined} from '@ant-design/icons';

import {useAppSelector} from '../../../hooks/useStore';
import {
    useGetTicketsQuery,
    useUpdateTicketMutation
} from '../../../store/api/ticketsApi';
import {useLazyGetSelectMemberQuery} from '../../../store/api/accountApi';
import {selectHasStaffRole, selectAccountMembershipNumber} from '../../../store/slices/userSlice';
import PageTitle from '../../../components/page/PageTitle';
import {messageType, showMessages} from '../../../helpers/message';
import {
    AccountNumberLinkProps,
    Ticket,
    TicketContext,
    TicketType,
    ticketContextMap,
    TicketStatus,
    ticketTypeMap,
    SelectedStatuses,
    FilterQueryParams,
    ticketStatusColors
} from '../../../types/ticketTypes';
import {BaseError} from '../../../types/apiTypes';
import {nothingToUpdate} from '../../../helpers/text';
import TicketFilter from './TicketFilter';
import TicketCommentsDrawer from './TicketCommentsDrawer';

import receiptIcon from '../../../assets/receipt.svg';

import './Tickets.less';

const {Title, Text} = Typography;

/**
 * A clickable link that triggers selecting a member by account number.
 * On success, it navigates to the account information page.
 */
const AccountNumberLink = ({accountNumber}: AccountNumberLinkProps) => {
    const navigate = useNavigate();
    const accountMembershipNumber = useAppSelector(selectAccountMembershipNumber);
    const [selectMember, {isSuccess, isError, error}] = useLazyGetSelectMemberQuery();

    React.useEffect(() => {
        if (accountMembershipNumber && isSuccess) {
            showMessages("You've selected member " + accountMembershipNumber, messageType.success);
            navigate('/account/account_info');
        }
        if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [accountMembershipNumber, isSuccess, isError, error, navigate]);

    return (
        <button
            onClick={() => accountNumber && selectMember({account_membership_number: accountNumber})}
            className={!accountNumber ? "ticket-no-membership-label" : "ticket-unstyled-button"}
        >
            {accountNumber ?? 'N/A'}
        </button>
    );
};

const Tickets = () => {
    const compressRows = window.innerWidth <= 991;
    const isAdminMctFleet = useAppSelector(state => selectHasStaffRole(state, ['admin', 'member-care', 'developer', 'fleet']));

    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [ticket, setTicket] = React.useState<null| Ticket>(null);
    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(10);
    const [totalRows, setTotalRows] = React.useState(10);
    const [filters, setFilters] = React.useState<FilterQueryParams>({});
    const [selectedStatuses, setSelectedStatuses] = React.useState<SelectedStatuses>({});

    const {data: ticketsData, isFetching: ticketsLoading, isSuccess, isError, error} = useGetTicketsQuery({
        page,
        perPage,
        filters,
    });

    const [updateTicket, {
        isSuccess: ticketUpdateSuccess,
        isError: ticketUpdateError,
        error: updateError
    }] = useUpdateTicketMutation();

    React.useEffect(() => {
        if (isSuccess && ticketsData) {
            setTotalRows(ticketsData.meta.total);
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [ticketsData, error, isError, isSuccess]);

    React.useEffect(() => {
        if (ticketUpdateSuccess) {
            showMessages('Ticket status successfully updated.', messageType.success);
        } else if (ticketUpdateError && updateError) {
            showMessages((updateError as BaseError).data?.message, messageType.error);
        }
    }, [ticketUpdateSuccess, ticketUpdateError, updateError]);

    const handleSearch = (values: FilterQueryParams) => {
        // Remove undefined values from the form submission to ensure only valid filters are applied.
        const filterValues = _.omitBy(values, _.isUndefined);
        setFilters(filterValues);
        setPage(1);
    };

    const handleReset = () => {
        setFilters({});
        setPage(1);
    };

    const pageChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    };

    const handleStatusChange = (ticketNumber: number, newStatus: TicketStatus) => {
        if (selectedStatuses[ticketNumber] === newStatus) return;
        setSelectedStatuses((prev) => ({...prev, [ticketNumber]: newStatus}));
    };

    const handleSaveTicketChange = (ticket: Ticket) => {
        const newStatus = selectedStatuses[ticket.ticket_number];

        if (!newStatus || newStatus === ticket.status) {
            showMessages(nothingToUpdate, messageType.error);
            return;
        }

        updateTicket({
            ticketID: ticket.id,
            ticketData: {status: newStatus},
        });
    };

    const getStatusMenuItems = (ticketNumber: number): MenuProps['items'] => {
        const statuses: TicketStatus[] = ['new', 'in progress', 'resolved', 'archived'];

        return statuses.map((status) => ({
            label: (
                <Space>
                    <Badge
                        className="status-badge-style"
                        color={ticketStatusColors[status]}
                        text={_.startCase(status)}
                        dot
                    />
                </Space>
            ),
            // Remove all spaces from status to create a valid key.
            key: status.replace(/\s/g, ''),
            onClick: () => handleStatusChange(ticketNumber, status),
        }));
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setTicket(null);
    }

    let columns: TableColumnsType<Ticket> = [
        {
            title: <Text strong>ID</Text>,
            key: 'ticket_number',
            render: (ticket: Ticket) => <span className="long-text-wrap">{ticket.ticket_number}</span>,
        },
        {
            title: <Text strong>Created</Text>,
            dataIndex: undefined,
            key: 'created',
            render: (ticket: Ticket) => (
                <>
                    <div className="long-text-wrap">
                        {_.upperFirst(ticket.created_by)}
                    </div>
                    <div className="long-text-wrap">{dayjs(ticket.created_at).format('D MMM YYYY, h:mmA')}</div>
                </>
            ),
        },
        {
            title: <Text strong>Regarding</Text>,
            dataIndex: 'regarding',
            key: 'regarding',
            render: (regarding: string) => <span className="long-text-wrap">{regarding}</span>,
        },
        {
            title: <Text strong>Account #</Text>,
            dataIndex: 'account_number',
            key: 'account_number',
            align: 'center',
            render: (accountNumber: string) => <AccountNumberLink accountNumber={accountNumber}/>,
        },
        {
            title: <Text strong>Content</Text>,
            dataIndex: 'body',
            key: 'body',
            render: (content: string) => (
                <div className="long-text-wrap">{content.slice(0, 30)}...</div>
            ),
        },
        {
            title: <Text strong>Type</Text>,
            dataIndex: 'type',
            key: 'type',
            render: (types: string[], ticket: Ticket) => (
                <>
                    {types.map((type, index) => (
                        <Tag
                            key={`${ticket.ticket_number}-type-${index}`}
                            color={ticketTypeMap[type as TicketType].color}
                        >
                            {_.upperFirst(type)}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: <Text strong>Status</Text>,
            dataIndex: 'status',
            key: 'status',
            render: (status: TicketStatus, ticket: Ticket) => {
                const currentStatus = selectedStatuses[ticket.ticket_number] || status;

                return (
                    <Dropdown
                        className="status-dropdown"
                        menu={{items: getStatusMenuItems(ticket.ticket_number)}}
                    >
                        <button className="ticket-unstyled-button">
                            <Space>
                                <Badge
                                    className="status-badge-style"
                                    color={ticketStatusColors[currentStatus]}
                                    text={_.startCase(currentStatus)}
                                    dot
                                />
                                <DownOutlined/>
                            </Space>
                        </button>
                    </Dropdown>
                );
            },
        },
        {
            title: <Text strong>Context</Text>,
            dataIndex: 'context',
            key: 'context',
            render: (contexts: string[], ticket: Ticket) => (
                <>
                    {contexts.map((context, index) => (
                        <Tag
                            key={`${ticket.ticket_number}-context-${index}`}
                            color={ticketContextMap[context as TicketContext].color}
                        >
                            {_.upperFirst(context)}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: <Text strong>Action</Text>,
            key: 'action',
            fixed: 'right',
            render: (_, ticket: Ticket) => (
                <>
                <Button
                    className="ticket-unstyled-button"
                    onClick={() => handleSaveTicketChange(ticket)}
                >
                    Save
                </Button>
                <Button
                    className="ticket-unstyled-button"
                    onClick={() => {
                        setDrawerOpen(true);
                        setTicket(ticket)
                    }}
                >
                    Comment
                </Button>
                </>
            ),
        }
    ];

    const TicketsHeader = (
        <Row className="ticket-manage-header-row">
            <Title className="ticket-manage-list-title" level={5}>Tickets</Title>
        </Row>
    );

    // Expanded row renderer for regular screens: show only the full Content.
    const regularExpandedRowRender = (record: Ticket) => (
        <div className="expanded-content">
            <Text strong>Full Content: </Text><br/>
            <span>{record.body}</span>
        </div>
    );

    const mobileExpandedRowRender = (record: Ticket) => {
        const expandRows = [
            <li key={0} className="tickets-expanded-row-list-item">
                <Text strong className="tickets-expanded-row-list-title">Created: </Text>
                {_.upperFirst(record.created_by)}{' '}
                {dayjs(record.created_at).format('D MMM YYYY, h:mmA')}
            </li>,
            <li key={1} className="tickets-expanded-row-list-item">
                <Text strong className="tickets-expanded-row-list-title">Account #: </Text>
                <AccountNumberLink accountNumber={record.account_number}/>
            </li>,
            <li key={2} className="tickets-expanded-row-list-item">
                <Text strong className="tickets-expanded-row-list-title">Content: </Text><br/>
                <span className="expanded-content">{record.body}</span>
            </li>,
            <li key={3} className="tickets-expanded-row-list-item">
                <Text strong className="tickets-expanded-row-list-title">Type: </Text>
                {record.type.map((type, index) => (
                    <Tag key={`${record.ticket_number}-type-${index}`} className="ant-tag-geekblue">
                        {_.upperFirst(type)}
                    </Tag>
                ))}
            </li>,
            <li key={4} className="tickets-expanded-row-list-item">
                <Text strong className="tickets-expanded-row-list-title">Context: </Text>
                {record.context.map((context, index) => (
                    <Tag key={`${record.ticket_number}-context-${index}`} className="ant-tag-geekblue">
                        {_.upperFirst(context)}
                    </Tag>
                ))}
            </li>,
            <li key={5} className="tickets-expanded-row-list-item">
                <Text strong className="tickets-expanded-row-list-title">Action: </Text>
                <Button
                    type="primary" ghost size="small"
                    onClick={() => handleSaveTicketChange(record)}
                >
                    Save
                </Button>
                <Button
                    className="tickets-expanded-row-comment-button" type="primary" ghost size="small"
                    onClick={() => {
                                setDrawerOpen(true);
                                setTicket(record)
                            }}
                >
                    Comment
                </Button>
            </li>
        ];

        return <ul className="tickets-expanded-row-list">{expandRows}</ul>;
    };

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        onChange: pageChange,
        onShowSizeChange: pageChange,
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
    };

    // For mobile, show only the ID, Reported By, and Context columns.
    if (compressRows) {
        columns = [columns[0], columns[2], columns[6]];
    }

    return (
        <>
            <div className="tickets-page-title">
                <PageTitle title="Tickets" icon={<img src={receiptIcon} alt="Ticket icon"/>}/>
            </div>
            <TicketFilter onSearch={handleSearch} onReset={handleReset}/>
            {isAdminMctFleet ? (
                <>
                    <Table
                        rowKey="ticket_number"
                        className="staff-tickets-table"
                        loading={ticketsLoading}
                        dataSource={ticketsData?.data}
                        columns={columns}
                        expandable={{
                            expandedRowRender: compressRows ? mobileExpandedRowRender : regularExpandedRowRender,
                        }}
                        pagination={paginationAttr}
                        bordered={false}
                        caption={TicketsHeader}
                        scroll={window.innerWidth <= 768 ? undefined : {x: 'max-content'}}
                    />
                    {ticket?.id ?
                        <TicketCommentsDrawer
                            ticket={ticket}
                            drawerOpen={drawerOpen}
                            handleClose={handleCloseDrawer}
                        />
                        : null
                    }
                </>
            ) : (
                <Text className="role-disclaimer-text">
                    Sorry, you do not have the required permissions to view this page.
                </Text>
            )}
        </>
    );
};

export default Tickets;
